import { renderPartInProgress } from 'fym-shared/src/music24/fullVideoPlan';
import type { LyricSyncState } from 'fym-shared/src/music24/lyricTiming';
import { isFullVideoTheme, isStudioThemeId, STUDIO_THEMES } from 'fym-shared/src/music24/studioThemes';
import { SerializedKitAsset } from '../../../../redux/api/fymAPI.types';

/**
 * The progress model of a full video and of the motion artwork (spec 2026-10-05 §6.6; design s10).
 * Every function is pure.
 */

type AssetFields = Pick<
  SerializedKitAsset,
  'themeId' | 'status' | 'stage' | 'renderPartsTotal' | 'renderPartsDone' | 'etaSec'
>;

/** True for the visualizer, the lyric video and the music video. */
export function isFullVideoAsset(asset: Pick<SerializedKitAsset, 'themeId'>): boolean {
  return isStudioThemeId(asset.themeId) && isFullVideoTheme(asset.themeId);
}

/** True for the motion artwork (Apple Music, square and tall). The Composer never posts it. */
export function isMotionArtworkAsset(asset: Pick<SerializedKitAsset, 'themeId'>): boolean {
  return asset.themeId === 'motionArtwork';
}

/**
 * The silent loop that the UI plays for the motion artwork: the MP4 file itself. An old row (a
 * ProRes MOV from before 2026-10-07) plays its H.264 preview, because a browser does not play a
 * MOV. Null for any other asset and for a version with no file.
 */
export function motionLoopUrl(
  asset: Pick<SerializedKitAsset, 'themeId' | 'status' | 'fileUrl' | 'previewUrl'>
): string | null {
  if (!isMotionArtworkAsset(asset) || asset.status !== 'ready') {
    return null;
  }
  return asset.previewUrl ?? asset.fileUrl;
}

/** The time left in whole minutes, rounded up and at least 1; null with no estimate. */
export function etaMinutes(etaSec: number | null | undefined): number | null {
  if (etaSec === null || etaSec === undefined || !Number.isFinite(etaSec) || etaSec < 0) {
    return null;
  }
  return Math.max(1, Math.ceil(etaSec / 60));
}

export interface RenderParts {
  /** The part in progress, 1-based ("Rendering part 4 of 8" while 3 parts are done). */
  part: number;
  total: number;
  /** True when every part is done: the join and the spec check run. */
  isJoining: boolean;
}

/** The parts of a full video that renders; null for every other asset and before the parts exist. */
export function renderPartsOf(asset: AssetFields): RenderParts | null {
  if (asset.status !== 'rendering') {
    return null;
  }
  const total = asset.renderPartsTotal ?? null;
  const part = renderPartInProgress(asset.renderPartsDone, total);
  if (total === null || part === null) {
    return null;
  }
  return { part, total, isJoining: (asset.renderPartsDone ?? 0) >= total };
}

export type FullVideoStageId = 'lyrics' | 'song' | 'plan' | 'render' | 'join';
export type FullVideoStageState = 'done' | 'now' | 'wait';

export interface FullVideoStage {
  id: FullVideoStageId;
  state: FullVideoStageState;
}

const POSITION: Record<Exclude<FullVideoStageId, 'lyrics'>, number> = { song: 0, plan: 1, render: 2, join: 3 };

/** Where the asset is: -1 in the queue, then the index of `POSITION`; 4 when it is ready. */
function positionOf(asset: AssetFields): number {
  if (asset.status === 'ready') {
    return 4;
  }
  switch (asset.stage) {
    case 'song':
      return POSITION.song;
    case 'plan':
      return POSITION.plan;
    case 'render':
      return renderPartsOf(asset)?.isJoining === true ? POSITION.join : POSITION.render;
    case 'queued':
    case null:
      return asset.status === 'rendering' ? POSITION.render : -1;
  }
}

function lyricsState(position: number, sync: LyricSyncState | undefined): FullVideoStageState {
  if (sync === 'synced' || position >= POSITION.render) {
    return 'done';
  }
  if (sync === 'syncing') {
    return 'now';
  }
  // An older API has no sync state: the plan starts only after the sync.
  return sync === undefined && position >= POSITION.plan ? 'done' : 'wait';
}

/**
 * The stage list of the Studio result (design s10): "Synced the lyrics" only for an asset type
 * that reads the lyrics (`needsLyrics`), then the song, the plan, the render in parts, and the join
 * with the spec check. In the queue no stage is "now".
 */
export function fullVideoStages(asset: AssetFields, lyricSync?: LyricSyncState): FullVideoStage[] {
  const position = positionOf(asset);
  const stateAt = (index: number): FullVideoStageState =>
    index < position ? 'done' : index === position ? 'now' : 'wait';
  const stages: FullVideoStage[] = [
    { id: 'song', state: stateAt(POSITION.song) },
    { id: 'plan', state: stateAt(POSITION.plan) },
    { id: 'render', state: stateAt(POSITION.render) },
    { id: 'join', state: stateAt(POSITION.join) },
  ];
  const needsLyrics = isStudioThemeId(asset.themeId) && STUDIO_THEMES[asset.themeId].needsLyrics;
  return needsLyrics ? [{ id: 'lyrics', state: lyricsState(position, lyricSync) }, ...stages] : stages;
}
